import { useEffect } from "react";
import { ArrowRight, MapPin } from "lucide-react";
import { ServiceSchema, FAQSchema, SpeakableSchema, setCanonical, setMetaTags } from "@/components/seo/SchemaMarkup";

const STORAJINI_URL = "https://storajini.com";

const ALL_CITIES_FAQS = [
  { question: "Does Storajini cover all cities in the Philippines?", answer: "Storajini is expanding across the Philippines. While we have verified listings in many major cities, new areas are being added regularly. Visit storajini.com to search for spaces in your specific location." },
  { question: "What if there are no listings in my city?", answer: "If your city doesn't have listings yet, you can sign up for notifications on storajini.com when spaces become available. You can also check nearby cities — many hosts serve surrounding areas." },
  { question: "Can I list a space in any Philippine city?", answer: "Yes! If you have unused storage space, a garage, spare room, or parking spot anywhere in the Philippines, you can list it on Storajini and start earning. There are no restrictions on location." },
  { question: "How do I find storage near me?", answer: "Visit storajini.com and use the search tool. Enter your city or area and browse available storage and parking spaces nearby. You can filter by size, price, and features." },
];

export default function AllCitiesPage() {
  useEffect(() => {
    setCanonical("/all-cities");
    setMetaTags({
      title: "Storage & Parking in All Philippine Cities | Storajini Guide",
      description: "Find storage and parking solutions anywhere in the Philippines. Browse spaces in any city on Storajini.com — from Metro Manila to Mindanao.",
    });
  }, []);

  return (
    <div data-testid="all-cities-page">
      <ServiceSchema cityName="Philippines" serviceType="Storage & Parking" description="Find storage and parking solutions anywhere in the Philippines." />
      <FAQSchema faqs={ALL_CITIES_FAQS} />
      <SpeakableSchema selectors={["h1", ".speakable-intro"]} />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-amber-900 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300 font-medium">All of the Philippines</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
              Storage & Parking Anywhere in the Philippines
            </h1>
            <p className="speakable-intro text-lg text-gray-300 leading-relaxed mb-8">
              Can't find your city in our guides? No problem! Storajini.com has storage and parking listings growing across the entire Philippines. Search for spaces in any city or barangay.
            </p>
            <a
              href={`${STORAJINI_URL}/search`}
              data-testid="all-cities-hero-cta"
              className="inline-flex items-center px-7 py-3.5 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-all"
            >
              Search All Cities on Storajini <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* What We Cover */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">What You Can Find</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Storage Spaces", desc: "Spare rooms, garages, warehouses, and dedicated storage facilities in cities across the Philippines." },
              { title: "Parking Spots", desc: "Open parking, covered garages, motorcycle parking, and long-term vehicle storage options." },
              { title: "Moving Help", desc: "Temporary storage during moves, flexible rental terms, and spaces available by the day, week, or month." },
            ].map((item, i) => (
              <div key={i} className="p-6 bg-gray-50 rounded-xl border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gray-50">
        <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Need Help Finding Space?</h2>
            <p className="text-gray-500 mb-6">Search for storage and parking spaces anywhere in the Philippines on Storajini.com.</p>
            <a
              href={`${STORAJINI_URL}/search`}
              data-testid="all-cities-cta-btn"
              className="inline-flex items-center px-8 py-4 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-600 transition-colors text-lg"
            >
              Get Free Quotes <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {ALL_CITIES_FAQS.map((faq, i) => (
              <details key={i} className="group bg-gray-50 rounded-xl border border-gray-100">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.question}</span>
                </summary>
                <div className="px-6 pb-4">
                  <p className="text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-14 bg-amber-500">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Start Your Search Now</h2>
          <p className="text-amber-100 mb-6">Find verified storage and parking spaces across the Philippines</p>
          <a
            href={`${STORAJINI_URL}/search`}
            data-testid="all-cities-bottom-cta"
            className="inline-flex items-center px-8 py-4 bg-white text-amber-600 font-bold rounded-lg hover:bg-gray-50 transition-colors"
          >
            Browse Spaces on Storajini <ArrowRight className="ml-2 w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
}
